import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { Typography } from '@mui/material';
import { ICustomFieldByObjectType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { useTranslations } from 'hooks/useTranslations';
import { ETransactionTypes } from 'constants/TransactionTypes';
import { TRANSACTION_ILLEGAL } from 'pages/LegalEntitiesPage/LegalEntityAddFirearmPage';

interface IAddFirearmListItemProps {
  index: number;
  arrayName?: string;
  removeItem: () => void;
  fields: any[];
  isShowManufacturedDate: boolean;
  control: any;
  errors: any;
  setValue: any;
  customFields?: ICustomFieldByObjectType[];
  getValues: any;
  isExhibitNumber: boolean;
  transactionTypeValue: any;
  isReloadCausedLocationSelect: boolean;
  isDataImportedFromFile: boolean;
  isImportMarking: boolean;
  isAppliedMarking: boolean;
  setIsDataImportedFromFile: (val: boolean) => void;
}

const AddFirearmListItem = ({
  index,
  arrayName,
  removeItem,
  fields,
  isShowManufacturedDate,
  control,
  setValue,
  errors,
  customFields = [],
  getValues,
  isExhibitNumber,
  transactionTypeValue,
  isReloadCausedLocationSelect,
  isDataImportedFromFile,
  isImportMarking,
  isAppliedMarking,
  setIsDataImportedFromFile,
}: IAddFirearmListItemProps) => {
  const { t } = useTranslations();
  const [checkboxStates, setCheckboxStates] = useState<{ [key: number]: boolean }>(() => ({
    [index]: getValues(`${arrayName}[${index}].isExhibitNumberChecked`) || false,
  }));
  const isTransactionIllegal = TRANSACTION_ILLEGAL.includes(transactionTypeValue);

  useEffect(() => {
    if (transactionTypeValue) {
      const typeUniqueIdValue = getValues('typeUniqueId');
      if (
        typeUniqueIdValue !== ETransactionTypes.SEIZED &&
        typeUniqueIdValue !== ETransactionTypes.SURRENDERED &&
        typeUniqueIdValue !== ETransactionTypes.RECOVERED
      ) {
        setValue(`${arrayName}[${index}].isExhibitNumberChecked`, false);
        setCheckboxStates((prevStates) => ({
          ...prevStates,
          [index]: false,
        }));
      }
    }
  }, [transactionTypeValue]);

  useEffect(() => {
    if (transactionTypeValue && !isReloadCausedLocationSelect && !isDataImportedFromFile) {
      setCheckboxStates({ [index]: false });
      setValue(`${arrayName}[${index}].isExhibitNumberChecked`, false);
      setValue(`${arrayName}[${index}].serialNumber`, '');
      setValue(`${arrayName}[${index}].serialNumberConfirmation`, '');
      setValue(`${arrayName}[${index}].exhibitNumber`, '');
      setValue(`${arrayName}[${index}].importMarking`, '');
      setValue(`${arrayName}[${index}].appliedMarking`, '');
      setValue(`${arrayName}[${index}].manufacturedAt`, '');

      customFields?.forEach((customField) =>
        setValue(`${arrayName}[${index}].${customField.name}`, ''),
      );
    }
    setIsDataImportedFromFile(false);
  }, [transactionTypeValue]);

  const handleCheckboxChange = () => {
    setCheckboxStates((prevStates) => ({
      ...prevStates,
      [index]: !prevStates[index],
    }));
    setValue(`${arrayName}[${index}].serialNumber`, '');
    setValue(`${arrayName}[${index}].serialNumberConfirmation`, '');
  };

  return (
    <Grid container sx={{ flexWrap: 'nowrap' }} columnSpacing={2}>
      <Grid sx={{ width: '40px', flexShrink: 0, pt: '30px' }}>
        <Typography variant="subtitle1" sx={{ mr: 2 }}>
          {index + 1}.
        </Typography>
      </Grid>
      <Grid container columnSpacing={2} sx={{ flex: 1 }}>
        {isExhibitNumber && (
          <Grid
            size={{ xs: 12, md: 6, lg: 4, xl: 3 }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormCheckbox
              name={`${arrayName}[${index}].isExhibitNumberChecked`}
              label={t('addFirearm.firearmDetails.section.serialNumberRemoved.label')}
              control={control}
              errors={errors}
              onChange={handleCheckboxChange}
            />
          </Grid>
        )}
        {checkboxStates && (
          <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
            <FormInput
              name={`${arrayName}[${index}].serialNumber`}
              label={`${t('addFirearm.firearmList.section.serialNumber.label')}${
                (isTransactionIllegal && !checkboxStates[index]) || !isTransactionIllegal
                  ? '*'
                  : ''
              }`}
              blurred
              control={control}
              setValue={setValue}
              errors={errors}
              arrayName={arrayName}
              arrayIndex={index}
              fieldName="serialNumber"
              charsLimit={20}
              disabled={checkboxStates[index]}
            />
          </Grid>
        )}
        {checkboxStates && (
          <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
            <FormInput
              name={`${arrayName}[${index}].serialNumberConfirmation`}
              label={`${t('addFirearm.firearmList.section.serialNumberConfirmation.label')}${
                (isTransactionIllegal && !checkboxStates[index]) || !isTransactionIllegal
                  ? '*'
                  : ''
              }`}
              control={control}
              setValue={setValue}
              errors={errors}
              arrayName={arrayName}
              disableCopyPaste
              arrayIndex={index}
              fieldName="serialNumberConfirmation"
              charsLimit={20}
              disabled={checkboxStates[index]}
            />
          </Grid>
        )}
        {isExhibitNumber && (
          <Grid size={{ xs: 12, sm: isShowManufacturedDate ? 6 : 12, md: 6, lg: 4, xl: 3 }}>
            <FormInput
              name={`${arrayName}[${index}].exhibitNumber`}
              label={`${t('addFirearm.firearmList.section.exhibitNumber.label')}${
                isTransactionIllegal && checkboxStates[index] ? '*' : ''
              }`}
              control={control}
              errors={errors}
              arrayName={arrayName}
              setValue={setValue}
              charsLimit={15}
              arrayIndex={index}
              fieldName="exhibitNumber"
            />
          </Grid>
        )}
        {isImportMarking && (
          <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
            <FormInput
              name={`${arrayName}[${index}].importMarking`}
              label={t('addFirearm.firearmList.section.importMarking.label')}
              control={control}
              errors={errors}
              arrayName={arrayName}
              setValue={setValue}
              arrayIndex={index}
              fieldName="importMarking"
              charsLimit={100}
            />
          </Grid>
        )}
        {isAppliedMarking && (
          <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
            <FormInput
              name={`${arrayName}[${index}].appliedMarking`}
              label={t('addFirearm.firearmList.section.appliedMarking.label')}
              control={control}
              errors={errors}
              arrayName={arrayName}
              setValue={setValue}
              arrayIndex={index}
              fieldName="appliedMarking"
              charsLimit={100}
            />
          </Grid>
        )}
        {isShowManufacturedDate && (
          <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }}>
            <FormDatePicker
              name={`${arrayName}[${index}].manufacturedAt`}
              label={`${t('addFirearm.firearmList.section.manufacturedDate.label')}`}
              control={control}
              errors={errors}
              arrayName={arrayName}
              disabled={transactionTypeValue === ETransactionTypes.DECLARED_MANUFACTURED}
              arrayIndex={index}
              fieldName="manufacturedAt"
            />
          </Grid>
        )}
        {customFields.map((customField) => (
          <Grid size={{ xs: 12, md: 6, lg: 4, xl: 3 }} key={customField.uniqueId}>
            <FormCustomField
              data={customField}
              control={control}
              setValue={setValue}
              errors={errors}
              arrayName={arrayName}
              arrayIndex={index}
            />
          </Grid>
        ))}
      </Grid>
      <Grid sx={{ width: '40px', flexShrink: 0 }}>
        {fields.length > 1 && (
          <Button
            variant={EButtonVariants.outlined}
            icon={EIconTypes.delete}
            onClick={removeItem}
            sizePX={EButtonSizesPX.px32}
            sx={{ marginTop: '28px' }}
          />
        )}
      </Grid>
    </Grid>
  );
};
export { AddFirearmListItem };
